import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Subscription, catchError, combineLatest, exhaustMap, filter, of, take, throwError, timeout, timer } from 'rxjs';
import { PortalAuthService } from 'src/app/core/auth/portal-auth.service';
import { isProfileMissingError } from 'src/app/core/auth/auth-errors';
import { PortalUser } from 'src/app/core/domain/portal-user.model';
import { AccountSettingsService } from 'src/app/core/services/account-settings.service';
import { UserApiService } from 'src/app/core/services/user-api.service';
import { IonicModule } from '@ionic/angular';
import { FormsModule } from '@angular/forms';
import { TooltipDirective } from '../../core/directives/tooltip.directive';

@Component({
    selector: 'app-onboarding',
    templateUrl: './onboarding.page.html',
    styleUrls: ['./onboarding.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IonicModule, FormsModule, TooltipDirective, TranslatePipe]
})
export class OnboardingPage implements OnInit, OnDestroy {
  /** How often the profile is asked for while registration runs in the background. */
  static readonly PROFILE_POLL_MS = 800;
  /** How long to wait for it before offering "check again" instead of a dead spinner. */
  static readonly PROFILE_WAIT_MS = 20000;

  firstName = '';
  lastName = '';
  displayName = '';
  email = '';
  loading = false;
  error = '';
  /** True while the profile is being saved or created in the background and the form is not shown. */
  autoCompleting = false;
  /** The profile did not show up in time: offer "check again" instead of the form. */
  waitFailed = false;

  private waitSub?: Subscription;

  constructor(
    private auth0: AuthService,
    private portalAuth: PortalAuthService,
    private userApi: UserApiService,
    private accountSettings: AccountSettingsService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Two sources, because Auth0's profile alone is not enough: a user who signed up through an
    // invitation set only an email and a password, so given_name/family_name come back empty and
    // the form would ask for a name the inviting admin already typed. The server matches the
    // caller against their pending invite and returns that name — an invited user therefore never
    // sees this form at all (see the auto-submit below). A failed prefill call is not fatal:
    // whoever registers without an invite (a brand-new tenant owner) fills the form in as before.
    combineLatest([
      this.auth0.user$.pipe(take(1)),
      this.userApi.getRegistrationPrefill().pipe(catchError(() => of(null))),
    ]).pipe(take(1)).subscribe(([user, prefill]) => {
      this.firstName = prefill?.firstName || user?.given_name || '';
      this.lastName = prefill?.lastName || user?.family_name || '';
      this.displayName = user?.nickname ?? '';
      this.email = prefill?.email || user?.email || '';

      if (prefill?.fromInvite && this.firstName && this.lastName) {
        this.autoCompleting = true;
        this.submit();
      }
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.waitSub?.unsubscribe();
  }

  submit(): void {
    if (!this.firstName || !this.lastName) {
      this.error = this.translate.instant('onboarding.nameRequired');
      return;
    }
    this.loading = true;
    this.userApi.completeRegistration({
      firstName: this.firstName,
      lastName: this.lastName,
      displayName: this.displayName || `${this.firstName} ${this.lastName}`,
      email: this.email,
    }).subscribe({
      next: () => this.waitForProfile(),
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        // Fall back to the form rather than leaving the invited user on a dead spinner.
        this.autoCompleting = false;
        if (err.status === 403) {
          this.error = this.translate.instant('onboarding.noCompanyError');
        } else if (err.status === 409) {
          // Profile already exists: navigate to home
          this.router.navigate(['/home']);
        } else {
          this.error = this.translate.instant('onboarding.saveError');
        }
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Registration is accepted at once but carried out in the background, so the profile does not exist yet
   * when the request returns. Going straight to /home made the shell load it too early ("not found"), which
   * read as "no company" and bounced the new member through the no-tenant page and a second sign-in. Waits
   * for the profile to appear, hands it to the shell, and only then opens the portal.
   */
  waitForProfile(): void {
    this.waitSub?.unsubscribe();
    this.loading = true;
    this.autoCompleting = true;
    this.waitFailed = false;
    this.error = '';
    this.cdr.markForCheck();

    this.waitSub = timer(0, OnboardingPage.PROFILE_POLL_MS).pipe(
      // exhaustMap: a slow answer is waited for, never cancelled by the next tick.
      exhaustMap(() => this.userApi.getMe().pipe(
        catchError((err: HttpErrorResponse) => isProfileMissingError(err) ? of(null) : throwError(() => err)),
      )),
      filter((user): user is PortalUser => user !== null),
      take(1),
      timeout(OnboardingPage.PROFILE_WAIT_MS),
    ).subscribe({
      next: user => {
        this.portalAuth.setAuthContext(user.tenantId ?? null, user.role ?? '', user.isPlatformAdmin ?? false, user.defaultOrgId);
        this.accountSettings.sync(user);
        // The shell loaded the profile before it existed; let it load it again (avatar menu, tenant name).
        this.portalAuth.refreshProfile();
        void this.router.navigate(['/home']);
      },
      error: () => {
        this.loading = false;
        this.autoCompleting = false;
        this.waitFailed = true;
        this.error = this.translate.instant('mainauth.onboarding.setupSlow');
        this.cdr.markForCheck();
      },
    });
  }
}
